import { getUserPreferences, type UserPreferences } from 'utils/helpers';
import {
  isMilestonesRoute,
  isRoadmapRoute,
  type ViewStateParams
} from 'utils/viewStateDeepLink';

const MILESTONES_PREFERENCES_KEY = 'milestonesGrid_preferences';

// Saved Views get their own storage, separate from `roadmap_user_preferences`
// (the shared Roadmap/My Stuff working state) — a Saved View's own filters
// must never overwrite the user's shared preferences, and vice versa.
const SAVED_VIEW_SNAPSHOT_KEY = 'roadmap_saved_view_snapshot';

function resolveCategoryProductFromParams(params: ViewStateParams) {
  const categories = params.categoryProductCategories?.length
    ? params.categoryProductCategories
    : params.categories ?? [];
  const products = params.categoryProductProducts?.length
    ? params.categoryProductProducts
    : params.products ?? [];

  return { categories, products };
}

/**
 * Build a full preferences snapshot from saved-view params, clearing prior view
 * state while preserving non-view UI prefs (expanded quarters/months, etc.).
 */
export function buildSavedViewPreferencesSnapshot(
  params: ViewStateParams,
  pathname: string
): UserPreferences {
  const existing = getUserPreferences();
  const { categories, products } = resolveCategoryProductFromParams(params);
  const surfaceFilters = params.features ?? [];

  const snapshot: UserPreferences = {
    ...existing,
    productGroups: params.productGroups ?? [],
    productGroupLabels: [],
    audiences: params.audiences ?? [],
    audienceLabels: [],
    categories,
    products,
    events: params.events ?? [],
    eventsLabel: [],
    features: surfaceFilters,
    status: params.status ?? [],
    lanesTiers: params.tiers ?? [],
    genAiFilters: params.genAiFilters ?? [],
    revenueImpactFilters: params.revenueImpactFilters ?? [],
    momentTypeFilters: params.momentTypeFilters ?? [],
    categoryProductCategories: categories,
    categoryProductProducts: products,
    surfaces: params.surfaces ?? [],
    mustWinDropdownSelection: null,
    hideEmptySwimLanes: false
  };

  if (params.startDate && params.endDate) {
    snapshot.dateRange = { start: params.startDate, end: params.endDate };
  }

  if (params.isCompact !== undefined) {
    snapshot.isCompact = params.isCompact;
  }
  if (params.showParkingLot !== undefined) {
    snapshot.showParkingLot = params.showParkingLot;
  }
  if (params.collapseAll !== undefined) {
    snapshot.collapseAll = params.collapseAll;
  }
  if (params.unNestAll !== undefined) {
    snapshot.isUnNested = params.unNestAll;
  }

  if (isRoadmapRoute(pathname)) {
    if (params.timelineViewMode) {
      snapshot.timelineViewMode = params.timelineViewMode;
    }
    if (params.timelineIconsVisible !== undefined) {
      snapshot.timelineIconsVisible = params.timelineIconsVisible;
    }

    if (params.mustWin !== undefined) {
      snapshot.mustWinViewActive = params.mustWin;
      if (!params.mustWin) {
        snapshot.mustWinDropdownSelection = null;
        snapshot.hideEmptySwimLanes = false;
      } else if (params.mustWinValue) {
        snapshot.mustWinDropdownSelection = params.mustWinValue;
        snapshot.hideEmptySwimLanes = true;
      } else {
        snapshot.mustWinViewActive = true;
      }
    }
  }

  if (isMilestonesRoute(pathname) && params.showBetaView !== undefined) {
    snapshot.showBetaView = params.showBetaView;
  }

  return snapshot;
}

export function persistSavedViewPreferences(
  params: ViewStateParams,
  pathname: string
): void {
  try {
    localStorage.setItem(
      SAVED_VIEW_SNAPSHOT_KEY,
      JSON.stringify(buildSavedViewPreferencesSnapshot(params, pathname))
    );
  } catch {
    // Silently fail if localStorage is unavailable
  }

  if (isMilestonesRoute(pathname)) {
    persistMilestonesGridPreferencesFromParams(params);
  }
}

/** Clear the persisted Saved View snapshot — call when leaving a Saved View. */
export function clearSavedViewSnapshot(): void {
  try {
    localStorage.removeItem(SAVED_VIEW_SNAPSHOT_KEY);
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

function persistMilestonesGridPreferencesFromParams(
  params: ViewStateParams
): void {
  try {
    const preferences = {
      name:
        params.sortColumn === 'name' && params.sortOption
          ? [params.sortOption]
          : [],
      sharedSort:
        params.sortColumn && params.sortColumn !== 'name'
          ? params.sortOption
          : undefined,
      sharedSortColumn:
        params.sortColumn && params.sortColumn !== 'name'
          ? params.sortColumn
          : undefined,
      sharedPin: params.pinOption,
      sharedPinColumn: params.pinColumn,
      isMetaTableCollapsed: params.isMetaTableCollapsed ?? false
    };
    localStorage.setItem(
      MILESTONES_PREFERENCES_KEY,
      JSON.stringify(preferences)
    );
  } catch {
    // Silently fail if localStorage is unavailable
  }
}
